import { openCartDrawerFromUpsell, syncCartCountFromServer } from "./cartDrawer/cartDrawer";
import { registerSwiperForStuckTouchReset, unregisterSwiperForStuckTouchReset } from "./ekSwiperSlider";
import { shouldShowDeliveryTime } from "./ekUpsellDrawerUtils";

declare global {
  interface Window {
    Swiper: any;
  }
}

type DrawerState = "closed" | "opening" | "open" | "closing" | "handoff";

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
const TOAST_MS = 4200;
const PANEL_MS = 300;
const CART_ADD_ROOT = () => (window as Window & { Shopify?: { routes?: { root?: string } } }).Shopify?.routes?.root ?? "/";

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const animationMs = () => (prefersReducedMotion() ? 0 : PANEL_MS);

class EkUpsellDrawer extends HTMLElement {
  #state: DrawerState = "closed";
  #lastFocus: HTMLElement | null = null;
  #toastTimer: number | null = null;
  #toastGen = 0;
  #adding = false;
  #actionsObserver: IntersectionObserver | null = null;

  connectedCallback() {
    this.addEventListener("click", this.#onClick);
    this.addEventListener("keydown", this.#onKeydown);
    document.addEventListener("keydown", this.#onDocumentKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
    this.removeEventListener("keydown", this.#onKeydown);
    document.removeEventListener("keydown", this.#onDocumentKeydown);
    this.#clearToastTimer();
    this.#destroyGridImageSwipers();
    this.#teardownStickyActions();
    if (this.#state !== "closed") this.#unlockPageScroll();
  }

  hasItems() {
    return this.dataset.hasItems === "true" && Boolean(this.#overlay() && this.#panel());
  }

  open() {
    if (!this.hasItems()) return false;
    if (this.#state === "opening" || this.#state === "open" || this.#state === "handoff") return true;

    const overlay = this.#overlay();
    const panel = this.#panel();
    if (!overlay || !panel) return false;

    this.#lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#hideError();
    this.#hideToast(true);
    this.#applyDeliveryVisibility();

    this.#state = "opening";
    overlay.classList.remove("pointer-events-none", "hidden");
    panel.setAttribute("aria-hidden", "false");
    this.#lockPageScroll();
    void syncCartCountFromServer();

    const reveal = () => {
      overlay.classList.remove("bg-black/0");
      overlay.classList.add("bg-black/30");
      panel.classList.remove("translate-y-full", "md:translate-x-full");
      this.#initGridImageSwipers();
      this.#syncStickyActions();
      window.setTimeout(() => {
        if (this.#state !== "opening") return;
        this.#state = "open";
        this.#focusInitial();
      }, animationMs());
    };

    if (prefersReducedMotion()) reveal();
    else requestAnimationFrame(() => requestAnimationFrame(reveal));

    return true;
  }

  close(options?: { animate?: boolean; restoreScroll?: boolean }) {
    const animate = options?.animate !== false && !prefersReducedMotion();
    const restoreScroll = options?.restoreScroll !== false;
    const overlay = this.#overlay();
    const panel = this.#panel();
    if (!overlay || !panel) {
      this.#state = "closed";
      return;
    }
    if (this.#state === "closed") return;

    this.#clearToastTimer();
    this.#hideToast(true);
    this.#teardownStickyActions();

    const finish = () => {
      overlay.classList.add("hidden", "pointer-events-none");
      overlay.classList.remove("bg-black/30");
      overlay.classList.add("bg-black/0");
      panel.classList.add("translate-y-full", "md:translate-x-full");
      panel.setAttribute("aria-hidden", "true");
      if (restoreScroll) this.#unlockPageScroll();
      this.#state = "closed";
      this.#restoreFocus();
    };

    if (!animate || this.#state === "handoff") {
      finish();
      return;
    }

    this.#state = "closing";
    panel.classList.add("translate-y-full", "md:translate-x-full");
    overlay.classList.remove("bg-black/30");
    overlay.classList.add("bg-black/0");
    window.setTimeout(finish, animationMs());
  }

  #lockPageScroll() {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overscrollBehavior = "none";
    document.body.style.overscrollBehavior = "none";
  }

  #unlockPageScroll() {
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    document.documentElement.style.overscrollBehavior = "";
    document.body.style.overscrollBehavior = "";
  }

  #overlay() {
    return this.querySelector<HTMLElement>("[data-upsell-overlay]");
  }

  #panel() {
    return this.querySelector<HTMLElement>("[data-upsell-panel]");
  }

  #toast() {
    return this.querySelector<HTMLElement>("[data-upsell-toast]");
  }

  #onClick = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target === this.#overlay()) {
      this.close();
      return;
    }
    if (target.closest("[data-upsell-close], [data-upsell-continue]")) {
      event.preventDefault();
      this.close();
      return;
    }
    if (target.closest("[data-upsell-go-cart]")) {
      event.preventDefault();
      this.#handoffToCart();
      return;
    }
    if (target.closest("[data-upsell-toast-dismiss]")) {
      event.preventDefault();
      this.#hideToast();
      return;
    }
    const moreSwatches = target.closest<HTMLButtonElement>("[data-upsell-swatches-more]");
    if (moreSwatches) {
      event.preventDefault();
      moreSwatches.closest("[data-upsell-swatches]")?.classList.add("is-expanded");
      moreSwatches.setAttribute("aria-expanded", "true");
      return;
    }
    const swatch = target.closest<HTMLButtonElement>("[data-upsell-swatch]");
    if (swatch) {
      event.preventDefault();
      this.#selectSwatch(swatch);
      return;
    }
    const addButton = target.closest<HTMLButtonElement>("[data-upsell-add]");
    if (addButton) {
      event.preventDefault();
      void this.#addUpsell(addButton);
    }
  };

  #onDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    if (this.#state !== "open" && this.#state !== "opening") return;
    event.preventDefault();
    this.close();
  };

  #onKeydown = (event: KeyboardEvent) => {
    if (this.#state !== "open" && this.#state !== "opening") return;
    if (event.key === "Escape") return;
    if (event.key !== "Tab") return;
    const panel = this.#panel();
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #focusInitial() {
    const panel = this.#panel();
    const closeBtn = Array.from(panel?.querySelectorAll<HTMLElement>("[data-upsell-close]") ?? []).find((el) => el.offsetParent !== null);
    (closeBtn ?? panel)?.focus();
  }

  #restoreFocus() {
    const last = this.#lastFocus;
    this.#lastFocus = null;
    if (last && document.contains(last)) last.focus();
  }

  #handoffToCart() {
    if (this.#state !== "open" && this.#state !== "opening") return;
    this.#state = "handoff";
    this.#clearToastTimer();
    this.#hideToast(true);
    const onCartOpen = () => {
      window.removeEventListener("ek:cart-opened-from-upsell", onCartOpen);
      this.close({ animate: false, restoreScroll: false });
    };
    window.addEventListener("ek:cart-opened-from-upsell", onCartOpen);
    openCartDrawerFromUpsell();
    window.setTimeout(onCartOpen, animationMs() + 50);
  }

  #selectSwatch(swatch: HTMLButtonElement) {
    const card = swatch.closest<HTMLElement>("[data-upsell-item]");
    const host = card?.querySelector<HTMLElement>("[data-upsell-image-swiper]");
    const swiper = host && (host as HTMLElement & { swiper?: { slideTo: (index: number) => void; activeIndex: number; slides: ArrayLike<HTMLElement> } }).swiper;
    if (swiper && swatch.dataset.variantId) {
      const idx = Array.from(swiper.slides).findIndex((slide) => slide.dataset.variantId === swatch.dataset.variantId);
      if (idx >= 0) {
        if (swiper.activeIndex !== idx) swiper.slideTo(idx);
        else this.#applySwatch(swatch);
        return;
      }
    }
    this.#applySwatch(swatch);
  }

  #applySwatch(swatch: HTMLButtonElement) {
    const card = swatch.closest<HTMLElement>("[data-upsell-item]");
    if (!card) return;
    const variantId = swatch.dataset.variantId;
    if (!variantId) return;

    if (!card.querySelector("[data-upsell-image-swiper]")) {
      for (const img of Array.from(card.querySelectorAll<HTMLImageElement>("[data-upsell-media]"))) {
        if (swatch.dataset.image) img.src = swatch.dataset.image;
      }
    }
    for (const link of Array.from(card.querySelectorAll<HTMLAnchorElement>("[data-upsell-link]"))) {
      if (swatch.dataset.url) link.href = swatch.dataset.url;
    }
    for (const price of Array.from(card.querySelectorAll("[data-upsell-price]"))) {
      price.textContent = swatch.dataset.price || "";
    }
    for (const compare of Array.from(card.querySelectorAll<HTMLElement>("[data-upsell-compare]"))) {
      const value = swatch.dataset.compare || "";
      compare.textContent = value;
      compare.classList.toggle("hidden", value === "");
    }
    const label = card.querySelector<HTMLElement>("[data-upsell-label]");
    if (label) {
      label.textContent = swatch.dataset.label || "";
      label.classList.toggle("hidden", !swatch.dataset.label);
    }
    const add = card.querySelector<HTMLButtonElement>("[data-upsell-add]");
    if (add) {
      add.dataset.variantId = variantId;
      if (swatch.dataset.title) add.dataset.title = swatch.dataset.title;
    }
    for (const other of Array.from(card.querySelectorAll<HTMLElement>("[data-upsell-swatch]"))) {
      const selected = other === swatch;
      other.classList.toggle("is-selected", selected);
      if (selected) other.setAttribute("aria-current", "true");
      else other.removeAttribute("aria-current");
    }
  }

  async #addUpsell(button: HTMLButtonElement) {
    if (this.#adding || button.getAttribute("aria-busy") === "true") return;
    const variantId = Number(button.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    this.#adding = true;
    button.setAttribute("aria-busy", "true");
    button.disabled = true;
    this.#hideError();

    const properties: Record<string, string> = {};
    const delivery = button.dataset.delivery?.trim();
    if (delivery) properties.Lieferzeit = delivery;

    try {
      const response = await fetch(`${CART_ADD_ROOT()}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties }],
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.description || payload?.message || "add_failed");
      }
      await syncCartCountFromServer();
      button.dataset.added = "true";
      window.setTimeout(() => button.removeAttribute("data-added"), 900);
      this.#showToast(button.dataset.title || "");
    } catch {
      this.#showError(this.#errorCopy());
    } finally {
      this.#adding = false;
      button.removeAttribute("aria-busy");
      button.disabled = false;
    }
  }

  #showToast(productName: string) {
    const toast = this.#toast();
    const message = toast?.querySelector("[data-upsell-toast-message]");
    if (!toast || !message) return;
    const template = this.dataset.toastTemplate || "__product__";
    const firstWord = (productName || "").trim().split(/\s+/)[0] || "";
    message.textContent = template.replace("__product__", firstWord).trim();
    this.#toastGen += 1;
    const gen = this.#toastGen;
    this.#clearToastTimer();
    toast.removeAttribute("hidden");
    toast.setAttribute("aria-hidden", "false");
    toast.classList.remove("is-hiding", "pointer-events-none");
    toast.classList.remove("is-visible");
    void toast.offsetWidth;
    toast.classList.add("is-visible");
    this.#toastTimer = window.setTimeout(() => {
      if (this.#toastGen === gen) this.#hideToast();
    }, TOAST_MS);
  }

  #hideToast(immediate = false) {
    const toast = this.#toast();
    if (!toast) return;
    const visible = toast.classList.contains("is-visible") || toast.classList.contains("is-hiding");
    if (!immediate && !visible) return;
    this.#clearToastTimer();
    const finish = () => {
      toast.classList.remove("is-visible", "is-hiding");
      toast.classList.add("pointer-events-none");
      toast.setAttribute("aria-hidden", "true");
    };
    if (immediate || prefersReducedMotion() || !toast.classList.contains("is-visible")) {
      this.#toastGen += 1;
      finish();
      return;
    }
    const gen = this.#toastGen;
    toast.classList.add("is-hiding");
    toast.classList.remove("is-visible");
    window.setTimeout(() => {
      if (this.#toastGen === gen) finish();
    }, 280);
  }

  #clearToastTimer() {
    if (this.#toastTimer == null) return;
    window.clearTimeout(this.#toastTimer);
    this.#toastTimer = null;
  }

  #showError(message: string) {
    const error = this.querySelector<HTMLElement>("[data-upsell-error]");
    if (!error) return;
    error.textContent = message;
    error.classList.remove("hidden");
  }

  #hideError() {
    const error = this.querySelector<HTMLElement>("[data-upsell-error]");
    if (!error) return;
    error.textContent = "";
    error.classList.add("hidden");
  }

  #errorCopy() {
    return this.dataset.errorCopy || "Das hat leider nicht geklappt. Bitte versuche es erneut.";
  }

  #applyDeliveryVisibility() {
    const deliveryEls = Array.from(this.querySelectorAll<HTMLElement>("[data-upsell-delivery]"));
    for (const el of deliveryEls) {
      if (!shouldShowDeliveryTime(el.textContent)) el.hidden = true;
    }
  }

  #scroll() {
    return this.querySelector<HTMLElement>("[data-upsell-scroll]");
  }

  #syncStickyActions() {
    this.#teardownStickyActions();
    const scroll = this.#scroll();
    const actions = this.querySelector<HTMLElement>("[data-upsell-actions]");
    const sticky = this.querySelector<HTMLElement>("[data-upsell-actions-sticky]");
    if (!scroll || !actions || !sticky) return;

    scroll.scrollTop = 0;
    this.#setStickyActions(false);
    this.#actionsObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const past = !entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0);
        this.#setStickyActions(past);
      },
      { root: scroll, threshold: 0 },
    );
    this.#actionsObserver.observe(actions);
  }

  #setStickyActions(stuck: boolean) {
    const scroll = this.#scroll();
    const sticky = this.querySelector<HTMLElement>("[data-upsell-actions-sticky]");
    if (!sticky) return;
    sticky.hidden = !stuck;
    sticky.setAttribute("aria-hidden", stuck ? "false" : "true");
    sticky.classList.toggle("pointer-events-none", !stuck);
    scroll?.classList.toggle("has-sticky-actions", stuck);
  }

  #teardownStickyActions() {
    this.#actionsObserver?.disconnect();
    this.#actionsObserver = null;
    this.#setStickyActions(false);
  }

  #initGridImageSwipers() {
    if (this.#state !== "opening" && this.#state !== "open") return;
    if (typeof window.Swiper !== "function") {
      window.setTimeout(() => this.#initGridImageSwipers(), 50);
      return;
    }

    for (const el of Array.from(this.querySelectorAll<HTMLElement>("[data-upsell-image-swiper]"))) {
      const host = el as HTMLElement & { swiper?: { update: () => void; slides: ArrayLike<HTMLElement>; activeIndex: number; on: (event: string, handler: () => void) => void } };
      if (host.swiper) {
        host.swiper.update();
        continue;
      }

      const prevButton = el.querySelector(".swiper-button-prev");
      const nextButton = el.querySelector(".swiper-button-next");
      const initial = Number.parseInt(el.dataset.initialIndex || "0", 10);
      const swiper = new window.Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide: Number.isFinite(initial) ? initial : 0,
        watchSlidesProgress: true,
        navigation: prevButton && nextButton ? { nextEl: nextButton, prevEl: prevButton, disabledClass: "swiper-button-disabled" } : false,
      });
      swiper.on("slideChange", () => {
        const slide = swiper.slides[swiper.activeIndex] as HTMLElement | undefined;
        const card = el.closest<HTMLElement>("[data-upsell-item]");
        const variantId = slide?.dataset.variantId;
        if (!card || !variantId) return;
        const swatch = card.querySelector<HTMLButtonElement>(`[data-upsell-swatch][data-variant-id="${variantId}"]`);
        if (swatch) this.#applySwatch(swatch);
      });
      registerSwiperForStuckTouchReset(swiper);
      requestAnimationFrame(() => {
        try {
          swiper.update();
        } catch {
          /* drawer width is known after the open transform */
        }
      });
    }
  }

  #destroyGridImageSwipers() {
    for (const el of Array.from(this.querySelectorAll<HTMLElement>("[data-upsell-image-swiper]"))) {
      const swiper = (el as HTMLElement & { swiper?: { destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void } }).swiper;
      if (!swiper) continue;
      unregisterSwiperForStuckTouchReset(swiper);
      try {
        swiper.destroy(true, true);
      } catch {
        /* already gone */
      }
    }
  }
}

if (!customElements.get("ek-upsell-drawer")) {
  customElements.define("ek-upsell-drawer", EkUpsellDrawer);
}

declare global {
  interface HTMLElementTagNameMap {
    "ek-upsell-drawer": EkUpsellDrawer;
  }
  interface WindowEventMap {
    "ek:cart-opened-from-upsell": Event;
  }
}

export { EkUpsellDrawer };
